import React, { useState } from 'react';
import { View, TouchableOpacity, StyleSheet, Animated, Platform } from 'react-native';
import { useReprocessFundingsMutation, useLazyDownloadFundingsQuery } from '@/store/api';
import { IFundingTableRow, IProcessedFunding } from '@/types/admin/funding';
import { processedFundingToTableRow } from '@/utils/admin/fundingUtils';
import { useAdminUser } from '@/hooks/admin/useAdminUser';
import { useToast } from '@/contexts/admin/ToastContext';
import { SVG_COMPONENTS } from '@/assets/admin';
import { ADMIN_COLORS } from '@/constants/admin';
import { useSpinAnimation } from '@/hooks/common/useSpinAnimation';


interface FundingSingleActionProps {
  funding: IFundingTableRow;
  onReprocessComplete: (updatedFunding: IFundingTableRow) => void;
}

export const FundingSingleAction: React.FC<FundingSingleActionProps> = ({
  funding,
  onReprocessComplete,
}) => {
  const { showSuccess, showError } = useToast();
  const adminUser = useAdminUser();
  
  const [reprocessFunding, { isLoading: isReprocessLoading }] = useReprocessFundingsMutation();
  const [triggerDownload] = useLazyDownloadFundingsQuery();
  const spin = useSpinAnimation(isReprocessLoading);
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);

  const handleReprocess = async () => {
    if (!adminUser.selectedOrganizationId) {
      showError('No organization selected');
      return;
    }

    try {
      const response = await reprocessFunding({
        ids: [funding.id],
        organizationId: adminUser.selectedOrganizationId,
      }).unwrap();

      // Update the row with new data from the response
      if (response && response.response && response.response.length > 0) {
        const updatedFundings = response.response;
        const processedFunding: IProcessedFunding = updatedFundings[0];
        const updatedFunding: IFundingTableRow = processedFundingToTableRow(processedFunding);
        onReprocessComplete(updatedFunding);
      }
      
      showSuccess('Reprocessing completed successfully!');
    } catch (error) {
      console.error('Failed to reprocess funding:', error);
      showError('Failed to reprocess funding. Please try again.');
    }
  };

  const handleDownload = async () => {
    if (!adminUser.selectedOrganizationId) {
      showError('No organization selected');
      return;
    }
    if (Platform.OS !== 'web') {
      showError('Download is available on web only');
      return;
    }

    try {
      setIsDownloadLoading(true);
      const blob = await triggerDownload({
        ids: [funding.id],
        organizationId: adminUser.selectedOrganizationId,
      }).unwrap();

      const filename = `funding_${funding.id}_${new Date().toISOString().split('T')[0]}.csv`;
      const g: any = globalThis as any;
      const url = g.URL?.createObjectURL(blob as unknown as Blob);
      if (url && g.document) {
        const link = g.document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        g.document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          g.URL?.revokeObjectURL(url);
          link.remove();
        }, 100);
      }

      showSuccess('Download started');
    } catch (error) {
      console.error('Failed to download funding CSV:', error);
      showError('Failed to download. Please try again.');
    } finally {
      setIsDownloadLoading(false);
    }
  };

  return (
    <View style={styles.container}>
      {/* Reprocess Button */}
      <TouchableOpacity
        style={[styles.actionButton, isReprocessLoading && styles.disabledButton]}
        onPress={handleReprocess}
        disabled={isReprocessLoading}
      >
        <Animated.View style={{ transform: [{ rotate: spin }] }}>
          <SVG_COMPONENTS.REFRESH 
            width={16} 
            height={16} 
            color={ADMIN_COLORS.PRIMARY} 
          />
        </Animated.View>
      </TouchableOpacity>

      {/* Download Button (web only) */}
      <TouchableOpacity
        style={[styles.actionButton, isDownloadLoading && styles.disabledButton]}
        onPress={handleDownload}
        disabled={isDownloadLoading}
      >
        <Animated.View>
          <SVG_COMPONENTS.DOWNLOAD 
            width={16} 
            height={16} 
            color={ADMIN_COLORS.PRIMARY}
          />
        </Animated.View>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  actionButton: {
    width: 32,
    height: 32,
    borderRadius: 6,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    alignItems: 'center',
    justifyContent: 'center',
  },
  disabledButton: {
    opacity: 0.6,
  },
});
